/**
 * The governed fact map behind the guided copy, following the `src/components/tour/tourFacts.ts`
 * discipline.
 *
 * Every value is a PRE-FORMATTED display string derived from the same `QoeAnalysis` and `QoeInput`
 * the panels render — no re-derivation, no arithmetic, so guided copy can never quote a number that
 * is not on screen. Facts the analysis cannot supply are simply ABSENT; the consumer falls back to
 * its own declared default, so a reader never meets a blank or a zero standing in for "unknown".
 *
 * Money arrives as exact decimal strings and is formatted by `formatQoeMoney`, which does string
 * arithmetic. Nothing here coerces an amount to a JS number.
 */
import { formatQoeMoney } from '../../../../../data/qoeApi'
import type { QoeAnalysis, QoeInput } from '../../../../../data/qoeTypes'
import { monthName } from './qoeFindingPresenter'

export type QoeFacts = Record<string, string>

/** The engagement shape the fact map reads. Narrower than `QoeDetail` so a test can build one by hand. */
export interface QoeFactSource {
  engagement: { name: string; revision: number }
  input: QoeInput
  analysis: QoeAnalysis
  packages?: ReadonlyArray<unknown> | null
}

const countOf = (v: number) => v.toLocaleString('en-US')
const windowLabel = (periods: readonly string[]) =>
  periods.length ? `${monthName(periods[0])} – ${monthName(periods[periods.length - 1])}` : null
const plural = (count: number, noun: string) => `${countOf(count)} ${noun}${count === 1 ? '' : 's'}`

export function buildQoeFacts(detail: QoeFactSource | null | undefined): QoeFacts {
  if (!detail) return {}
  const facts: QoeFacts = {}
  const put = (key: string, value: string | null | undefined) => {
    if (value != null && String(value).trim()) facts[key] = String(value).trim()
  }
  // `formatQoeMoney` answers "Unavailable" for a missing amount. A fact must be absent instead, so the
  // consumer's own fallback wording is what a reader sees.
  const money = (value: string | null | undefined) => (value == null || value === '' ? null : formatQoeMoney(value, detail.analysis.currency || 'USD'))

  const { analysis, input } = detail
  const settings = input.settings
  put('company', detail.engagement.name)
  put('revision', `Version ${countOf(detail.engagement.revision)}`)
  put('earnings_window', windowLabel(settings.earnings_periods))
  put('history_window', windowLabel(settings.periods))
  put('comparison_window', windowLabel(settings.comparative_periods))
  put('earnings_months', settings.earnings_periods.length ? plural(settings.earnings_periods.length, 'month') : null)
  put('entities', settings.entities.filter(e => e.in_perimeter).map(e => e.name).join(' · ') || null)
  put('source_files', input.source.manifests.length ? plural(input.source.manifests.length, 'file') : null)
  put('source_rows', analysis.source_row_count ? plural(analysis.source_row_count, 'row') : null)
  put('size_that_matters', money(settings.materiality))

  // Earnings are quoted only when the engine says the window is complete; the workpapers print
  // "Unavailable" in exactly this case, and guided copy must not disagree with them.
  if (analysis.earnings.complete) {
    const selected = analysis.earnings.selected
    put('revenue', money(selected.revenue))
    put('reported_earnings', money(selected.reported_ebitda))
    put('adjusted_earnings', money(selected.normalized_ebitda))
    put('corrected_earnings', money(selected.corrected_ebitda))
    put('one_off_and_owner_items', money(selected.historical_normalizations))
    put('accounting_corrections', money(selected.accounting_corrections))
  }

  const reconciled = analysis.reconciliations.filter(r => r.status === 'reconciled').length
  if (analysis.reconciliations.length) put('checks_reconciled', `${countOf(reconciled)} of ${countOf(analysis.reconciliations.length)}`)
  const supplied = analysis.coverage.filter(r => r.status === 'supplied').length
  if (analysis.coverage.length) put('months_supplied', `${countOf(supplied)} of ${countOf(analysis.coverage.length)}`)

  const exceptions = analysis.findings.filter(f => f.severity !== 'info')
  put('open_items', countOf(exceptions.length))
  put('blocking_items', countOf(exceptions.filter(f => f.severity === 'error').length))

  put('working_capital_target', money(analysis.nwc.selected_peg))
  put('closing_true_up', money(analysis.nwc.closing_adjustment))
  put('illustrative_equity_value', money(analysis.valuation.equity_value))
  put('multiple', analysis.valuation.multiple ? `${analysis.valuation.multiple}×` : null)
  const packages = detail.packages?.length || 0
  put('published_versions', packages ? plural(packages, 'version') : null)
  return facts
}

/**
 * Reads one fact, or the caller's declared default. The default is required: a guided sentence must
 * read correctly when the analysis cannot supply the number it wanted.
 */
export const qoeFact = (facts: QoeFacts, key: string, fallback: string): string => facts[key] ?? fallback
